/**
 * ================================================================================================
 *  פתיחת ארגון חדש — the first screen a stranger sees, and it creates nothing
 * ================================================================================================
 *
 * §10.2 · §9.6. Two things on one page, in the order a person needs them:
 *
 *   1. **WHAT IT COSTS** — the price, the cycle, the trial length and the date the first charge
 *      falls, read from the server. §9.6 asks for those four *"בבירור"*, and they are above the
 *      form rather than behind it: somebody should know the price before typing their address.
 *   2. **THE FORM** — an address and the organisation's name. Nothing else, because nothing else is
 *      needed to send a verification.
 *
 * ── IT IS PRE-SESSION, SO THERE IS NO SHELL ────────────────────────────────────────────────────
 *
 * `<main className="pre-session">`, like the sign-in and mailed-link screens. An `AppShell` would
 * draw a context bar, a rail and a company switcher for somebody with no account.
 *
 * ── AND IT NEVER SAYS WHETHER THE ADDRESS WAS KNOWN ────────────────────────────────────────────
 *
 * The server answers identically for a free address, one already in flight, and a mail that
 * failed — so this screen has one success state and it is deliberately vague about what happened
 * on the other side. A screen that said "we have sent you a link" versus "you already have a
 * registration in progress" would reconstruct the oracle the API refuses to be.
 */

import { useCallback, useState } from 'react';
import type { FormEvent, JSX } from 'react';

import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { StateBlock } from '../components/StateBlock';
import { classifyActionError, type ActionFailure } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';
import type { PublicPlanOfferView } from '../api/contract';
import { routes } from '../routes';

const CYCLE_LABEL: Readonly<Record<string, TextKey>> = {
  month: 'signup.cycle.month',
};

export function PublicSignupScreen(): JSX.Element {
  const { t } = useLocale();
  const [email, setEmail] = useState('');
  const [legalName, setLegalName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState<ActionFailure | null>(null);

  const read = useCallback(() => callOperation('public.signup.plans', {}), []);
  const offers = useServerData(read);

  function submit(event: FormEvent): void {
    event.preventDefault();
    setSending(true);
    setFailure(null);
    void callOperation('public.signup.start', {
      body: {
        email,
        legalName,
        // OMITTED rather than sent empty: the server treats an empty string as absent anyway, and
        // a body that carries a field it does not mean is a body somebody later reads as meaning it.
        ...(displayName.trim() === '' ? {} : { displayName: displayName.trim() }),
      },
    })
      .then(() => {
        setSent(true);
      })
      .catch((error: unknown) => {
        setFailure(classifyActionError(error));
      })
      .finally(() => {
        setSending(false);
      });
  }

  /*
   * A CLOSED DOOR IS NOT A FAILURE, and it must not render as one. The server answers 404 while
   * `self_signup_open` is not `true`, which on this estate is the ordinary state — so the read's
   * failure is inspected for it and answered with a sentence rather than a support code.
   */
  const closed =
    offers.result.state === 'failed' &&
    offers.result.failure.kind === 'notFound';

  /*
   * ── AND AN OPEN DOOR WITH NOTHING ON OFFER IS THE SAME DEAD END, ONE LEVEL IN ─────────────
   *
   * ו-16, 2026-10-30: *"בלי כפתור שמוביל למבוי סתום."*
   *
   * `self_signup_open = true` with no plan marked `is_public_signup` is a REACHABLE state and it is
   * the ordinary one on a fresh estate: 0032 seeds the door shut and the column arrives 0 on every
   * plan, so opening the door without marking a plan is one click out of two.
   *
   * This screen used to render the whole form in that state, with "אין כרגע תכנית שניתן
   * לרכוש" above it and a live submit button below it. That button WORKS: it opens a signup
   * request and mails a verification link, for a registration whose next step has nothing to
   * choose. A stranger would follow a mailed link to a wall.
   *
   * So the form is not drawn. One sentence instead, the same shape as the shut door, because from
   * the stranger's side the two states are the same fact: there is nothing here to buy today.
   */
  const nothingOnOffer = offers.result.state === 'loaded' && offers.result.data.plans.length === 0;

  return (
    <main className="pre-session" dir="rtl">
      <h1>{t('signup.title')}</h1>

      {closed ? (
        <StateBlock titleHe={t('signup.title')} bodyHe={t('signup.closed')} tone="empty" />
      ) : nothingOnOffer ? (
        <StateBlock titleHe={t('signup.title')} bodyHe={t('signup.noPlans')} tone="empty" />
      ) : sent ? (
        <StateBlock
          titleHe={t('signup.sentTitle')}
          bodyHe={t('signup.sentBody')}
          nextStep={
            <p>
              <a href={routes.signIn}>{t('signup.goSignIn')}</a>
            </p>
          }
        />
      ) : (
        <>
          <p className="screen-lead">{t('signup.lead')}</p>

          <section className="tile">
            <h2 className="breakdown__title">{t('signup.plansTitle')}</h2>
            {offers.result.state === 'loaded' ? (
              // `nothingOnOffer` took the empty case above, before the form was drawn at all. This
              // arm cannot be reached with an empty list and is kept narrow rather than deleted,
              // because `plans` is a server shape and a renderer that indexes it unguarded is how
              // a future zero-length answer becomes a blank panel.
              offers.result.data.plans.length === 0 ? null : (
                <ul className="list">
                  {offers.result.data.plans.map((offer) => (
                    <li key={offer.planCode} className="list__row">
                      <PlanOffer offer={offer} />
                    </li>
                  ))}
                </ul>
              )
            ) : offers.result.state === 'failed' ? (
              <ActionFailureNotice failure={offers.result.failure} />
            ) : (
              <p aria-live="polite">{t('signup.plansTitle')}</p>
            )}
          </section>

          {failure === null ? null : <ActionFailureNotice failure={failure} />}

          <form className="form" onSubmit={submit}>
            <label className="field">
              <span className="field__label">{t('signup.email')}</span>
              <input
                className="field__input"
                type="email"
                dir="ltr"
                required
                maxLength={320}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>

            <label className="field">
              <span className="field__label">{t('signup.legalName')}</span>
              <input
                className="field__input"
                required
                maxLength={200}
                value={legalName}
                onChange={(event) => setLegalName(event.target.value)}
              />
            </label>

            <label className="field">
              <span className="field__label">{t('signup.displayName')}</span>
              <input
                className="field__input"
                maxLength={200}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            </label>

            <div className="actions-row">
              <button type="submit" className="btn btn--primary" disabled={sending}>
                {t('signup.submit')}
              </button>
            </div>
          </form>
        </>
      )}
    </main>
  );
}

/**
 * §9.6'S FIGURES, AND EVERY ONE OF THEM COMES FROM THE SERVER.
 *
 * ══ WHAT WAS HERE, AND WHY THE OWNER SAW IT. 2026-10-30. ═════════════════════════
 *
 * This rendered `${offer.price} ${offer.currencyCode}` — `390.0000 ILS`, literally the shape he
 * named as wrong, on the FIRST SCREEN A STRANGER SEES. The comment above it said the price is
 * *"rendered as the server formatted it"*, which was true and was the problem: the server was
 * handing over the working four-decimal figure and nothing was formatting anything.
 *
 * `priceDisplay`, `vatDisplay` and `totalDisplay` are formatted by `formatAmount` server-side now,
 * and this screen prints them. It still does not format money — that rule was right.
 *
 * §4 ALSO ASKS FOR THE THREE FIGURES TOGETHER: *"הצג מחיר, מע״מ וסכום סופי לפני האישור."*
 * When no VAT rate is recorded they are all null and only the ex-VAT price is shown — the plan is
 * still sellable and that price is still true; what cannot be claimed is a total.
 */
function PlanOffer({ offer }: { readonly offer: PublicPlanOfferView }): JSX.Element {
  const { t } = useLocale();
  const cycle = CYCLE_LABEL[offer.periodKind];
  return (
    <div className="list__main">
      <h3 className="list__title">{offer.displayNameHe}</h3>
      {/*
        THE DIRECTION BELONGS ON THE NUMBER, NOT ON THE PARAGRAPH. **2026-11-04.**

        This carried `dir="ltr"`, to keep the digits in reading order -- and `dir` on a BLOCK sets
        that block's alignment too. `text-align: start` then resolved to the LEFT edge inside an
        RTL sheet, and the price sat 1500px from the plan it priced. `<bdi>` isolates the number's
        direction and leaves the paragraph in the sheet's.
      */}
      <p className="list__meta list__meta--lead">
        {/* THE READABLE PRICE. The raw figure is the fallback for an estate whose currency this
            build has no symbol for — `formatAmount` answers null only for an unparseable amount. */}
        <bdi>{offer.priceDisplay ?? `${offer.price} ${offer.currencyCode}`}</bdi>
      </p>
      {offer.totalDisplay === null ? null : (
        <p className="list__meta">
          {`${t('signup.vat')} ${offer.vatRatePct ?? ''}%: ${offer.vatDisplay ?? ''} · ${t('signup.total')}: ${offer.totalDisplay}`}
        </p>
      )}
      <p className="list__meta">
        {`${t('signup.cycle')}: ${cycle === undefined ? offer.periodKind : t(cycle)}`}
      </p>
      <p className="list__meta">
        {`${t('signup.trial')}: ${String(offer.trialDays)} ${t('signup.trialDays')}`}
      </p>
      <p className="list__meta">
        {`${t('signup.firstCharge')}: `}
        <bdi>{offer.firstChargeOn}</bdi>
      </p>
    </div>
  );
}
